'use client';

import { useLayoutEffect, type RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const PIN_GAP_PX = 16;

function getPinTop() {
  if (typeof window === 'undefined') return 104;

  const header = document.getElementById('site-header');
  if (header) {
    return header.getBoundingClientRect().height + PIN_GAP_PX;
  }

  const cssValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--site-header-height')
    .trim();
  const headerHeight = Number.parseFloat(cssValue);

  return (Number.isFinite(headerHeight) ? headerHeight : 88) + PIN_GAP_PX;
}

type GuideVerticalProgressRefs = {
  sectionRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  progressColumnRef: RefObject<HTMLElement | null>;
  progressPinRef: RefObject<HTMLElement | null>;
  progressRailRef: RefObject<HTMLElement | null>;
  progressFillRef: RefObject<HTMLElement | null>;
  progressDotRef: RefObject<HTMLElement | null>;
};

export function useGuideVerticalProgress(
  refs: GuideVerticalProgressRefs,
  refreshKey: string | number,
) {
  const {
    sectionRef,
    contentRef,
    progressColumnRef,
    progressPinRef,
    progressFillRef,
    progressDotRef,
  } = refs;

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const content = contentRef.current;
    const columnEl = progressColumnRef.current;
    const pinEl = progressPinRef.current;
    const fillEl = progressFillRef.current;
    const dotEl = progressDotRef.current;
    if (!section || !content || !columnEl || !pinEl || !fillEl || !dotEl) return;

    gsap.registerPlugin(ScrollTrigger);

    const getContentHeight = () =>
      Math.max(content.offsetHeight, content.getBoundingClientRect().height);

    const getTrackMetrics = () => {
      const contentHeight = getContentHeight();
      const dotSize = dotEl.offsetHeight || 8;
      const halfDot = dotSize / 2;
      const trackHeight = Math.max(0, contentHeight - dotSize);

      return { contentHeight, halfDot, trackHeight };
    };

    const syncLayout = () => {
      const { contentHeight, halfDot } = getTrackMetrics();

      columnEl.style.height = `${contentHeight}px`;
      pinEl.style.height = `${contentHeight}px`;
      fillEl.style.top = `${halfDot}px`;
    };

    const updateProgressUI = (progress: number) => {
      const p = Math.max(0, Math.min(1, progress));
      const { halfDot, trackHeight } = getTrackMetrics();
      const filled = Math.round(p * trackHeight);

      fillEl.style.height = `${filled}px`;
      dotEl.style.top = `${halfDot + filled}px`;
    };

    const mm = gsap.matchMedia();

    mm.add('(min-width: 1024px)', () => {
      const scrollTrigger = ScrollTrigger.create({
        trigger: content,
        start: () => `top top+=${getPinTop()}`,
        end: 'bottom bottom',
        scrub: 0.35,
        invalidateOnRefresh: true,
        onUpdate: (self) => updateProgressUI(self.progress),
        onLeave: () => updateProgressUI(1),
        onLeaveBack: () => updateProgressUI(0),
        onEnterBack: (self) => updateProgressUI(self.progress),
      });

      syncLayout();
      updateProgressUI(scrollTrigger.progress);

      const refresh = () => {
        syncLayout();
        ScrollTrigger.refresh();
        updateProgressUI(scrollTrigger.progress);
      };

      const resizeObserver =
        typeof ResizeObserver !== 'undefined' ? new ResizeObserver(refresh) : null;
      resizeObserver?.observe(content);

      section.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', refresh, { once: true });
      });

      window.addEventListener('resize', refresh);
      const refreshId = window.requestAnimationFrame(refresh);
      const delayedRefreshId = window.setTimeout(refresh, 300);

      return () => {
        window.cancelAnimationFrame(refreshId);
        window.clearTimeout(delayedRefreshId);
        window.removeEventListener('resize', refresh);
        resizeObserver?.disconnect();
        scrollTrigger.kill();
        columnEl.style.removeProperty('height');
        pinEl.style.removeProperty('height');
        fillEl.style.removeProperty('height');
        fillEl.style.removeProperty('top');
        dotEl.style.removeProperty('top');
      };
    });

    return () => mm.revert();
  }, [
    sectionRef,
    contentRef,
    progressColumnRef,
    progressPinRef,
    progressFillRef,
    progressDotRef,
    refreshKey,
  ]);
}
